import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { ScoreCard } from "@/components/ScoreCard";
import { PremiumLock } from "@/components/PremiumLock";
import { userFinancials, savedSearches, lenders, recommendationMeta } from "@/lib/mock-data";
import { usd } from "@/lib/format";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, AlertTriangle, CheckCircle2, TrendingUp, Sparkles, Search, ScanSearch, Trash2 } from "lucide-react";
import { listAnalyses, deleteAnalysis } from "@/utils/analyses.functions";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({ meta: [{ title: "Dashboard — LendyTrack" }] }),
  component: Dashboard,
});

function Dashboard() {
  const { isPremium } = useSubscription();
  const queryClient = useQueryClient();
  const { data: savedAnalyses = [] } = useQuery({
    queryKey: ["savedAnalyses"],
    queryFn: () => listAnalyses({ data: {} }),
    enabled: isPremium,
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteAnalysis({ data: { id } });
      queryClient.invalidateQueries({ queryKey: ["savedAnalyses"] });
      toast.success("Analysis deleted");
    } catch (err: any) {
      toast.error(err?.message ?? "Delete failed");
    }
  };
  return (
    <AppShell>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Overview</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Your borrowing snapshot</h1>
        <p className="mt-1 text-sm text-muted-foreground">A quick look at where you stand and what to do next.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ScoreCard label="Credit Health" score={Math.round((userFinancials.creditScore / 850) * 100)} caption={`FICO ${userFinancials.creditScore}`} />
        <ScoreCard label="Financial Wellness" score={userFinancials.financialWellness} caption="Below national median" />
        <ScoreCard label="Loan Readiness" score={userFinancials.loanReadiness} caption="Improve before applying" />
      </div>

      {/* Action plan */}
      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6 shadow-soft lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-accent">AI recommendations</div>
              <h2 className="mt-1 font-display text-xl font-semibold">Your 30-day improvement plan</h2>
            </div>
            <Link to="/health" className="text-xs font-medium text-primary hover:underline">View full plan →</Link>
          </div>
          <div className="mt-5 space-y-3">
            {[
              { icon: TrendingUp, title: "Lower credit utilization to 30%", body: `Currently at ${userFinancials.creditUtilization}%. Paying down $1,420 unlocks roughly +35 FICO points.`, tone: "bg-success/10 text-success" },
              { icon: AlertTriangle, title: "Avoid new hard inquiries", body: "Skip the OppLoans pre-qual. It will drop your score 5–10 pts and won't change approval odds.", tone: "bg-warning/15 text-warning-foreground" },
              { icon: CheckCircle2, title: "Build a $1,000 emergency fund", body: `You're at ${usd(userFinancials.emergencySavings)}. $42/wk for 16 weeks gets you there.`, tone: "bg-accent/15 text-accent-foreground" },
            ].map((r) => {
              const Icon = r.icon;
              return (
                <div key={r.title} className="flex gap-4 rounded-lg border border-border bg-background p-4">
                  <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg ${r.tone}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{r.title}</div>
                    <div className="text-xs text-muted-foreground">{r.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <Search className="h-3 w-3" /> Recent lender searches
            </div>
            <div className="mt-3 space-y-2">
              {savedSearches.map((s) => (
                <Link key={s} to="/lenders" search={{ q: s }} className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm hover:bg-secondary">
                  <span>{s}</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>

          <PremiumLock
            title="Saved loan analyses"
            description="Premium saves every offer you analyze so you can compare them later."
          >
            <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <ScanSearch className="h-3 w-3" /> Saved loan analyses
              </div>
              {savedAnalyses.length === 0 ? (
                <div className="mt-4 rounded-lg border border-dashed border-border bg-background p-4 text-center">
                  <p className="text-sm text-muted-foreground">No saved analyses yet.</p>
                  <Link
                    to="/analyzer"
                    className="mt-2 inline-block text-xs font-medium text-primary hover:underline"
                  >
                    Analyze and save your first loan →
                  </Link>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  {savedAnalyses.map((a: any) => {
                    const payload = a.payload as { amount: number; apr: number; term: number; verdict: string } | undefined;
                    return (
                      <div key={a.id} className="rounded-md border border-border bg-background p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold">{a.title}</div>
                            <div className="text-xs text-muted-foreground">
                              {payload ? `${usd(payload.amount)} · ${payload.apr}% APR · ${payload.term} mo` : "Loan analysis"}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDelete(a.id)}
                            className="rounded p-1.5 text-muted-foreground hover:bg-danger/10 hover:text-danger"
                            aria-label="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {payload && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            Verdict: <span className="font-medium text-foreground">{payload.verdict}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </PremiumLock>




          <Link to="/coach" className="block rounded-xl border border-border bg-gradient-hero p-5 text-primary-foreground shadow-soft hover:opacity-95">
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-accent/30">
                <Sparkles className="h-5 w-5 text-accent" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-accent">AI Coach</div>
                <div className="font-display text-base font-semibold">Ask anything about your loan</div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Featured lenders */}
      <div className="mt-10">
        <div className="flex items-end justify-between">
          <h2 className="font-display text-xl font-semibold">Lenders you've viewed</h2>
          <Link to="/lenders" className="text-xs font-medium text-primary hover:underline">Browse all →</Link>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {lenders.slice(0, 3).map((l) => {
            const meta = recommendationMeta[l.recommendation];
            return (
              <Link key={l.slug} to="/lenders/$slug" params={{ slug: l.slug }} className="rounded-xl border border-border bg-card p-5 shadow-soft hover:shadow-elevated">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-lg font-semibold">{l.name}</div>
                    <div className="text-xs text-muted-foreground">{l.category}</div>
                  </div>
                  <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${meta.bg} ${meta.tone}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                  <span>Trust {l.scores.trust}</span>
                  <span>·</span>
                  <span>APR {l.aprRange[0]}–{l.aprRange[1]}%</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
